import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * レイアウト状態
 */
interface LayoutState {
  /** サイドバー表示状態（lg以上での開閉） */
  sidebarVisible: boolean;
  /** Drawer開閉状態（md以下） */
  drawerOpen: boolean;
  /** 開いている地区アコーディオンの値 */
  districtAccordionValue: string[];
  /** 地区ごとの開いている生産者アコーディオンの値 */
  producerAccordionValues: Record<string, string[]>;
}

/**
 * レイアウトアクション
 */
interface LayoutActions {
  /** サイドバー表示/非表示を切り替え */
  toggleSidebar: () => void;
  /** サイドバー表示状態を設定 */
  setSidebarVisible: (visible: boolean) => void;
  /** Drawer開閉を切り替え */
  toggleDrawer: () => void;
  /** Drawerを閉じる */
  closeDrawer: () => void;
  /** Drawerを開く */
  openDrawer: () => void;
  /** 地区アコーディオンの開閉値を設定 */
  setDistrictAccordionValue: (value: string[]) => void;
  /** 特定地区の生産者アコーディオン開閉値を更新（functional updater で stale snapshot を回避） */
  updateProducerAccordion: (districtKey: string, value: string[]) => void;
}

/**
 * レイアウトストア型
 */
export type LayoutStore = LayoutState & LayoutActions;

/**
 * レイアウト状態管理ストア
 *
 * サイドバーの開閉状態をページ間で共有し、
 * sidebarVisibleのみをlocalStorageに永続化する。
 *
 * @example
 * ```tsx
 * const { sidebarVisible, toggleSidebar, drawerOpen, openDrawer } = useLayoutStore();
 *
 * // サイドバートグル（lg以上）
 * <button onClick={toggleSidebar}>Toggle Sidebar</button>
 *
 * // Drawer開閉（md以下）
 * <button onClick={openDrawer}>Open Menu</button>
 * ```
 */
export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set) => ({
      // 状態
      sidebarVisible: true,
      drawerOpen: false,
      districtAccordionValue: [],
      producerAccordionValues: {},

      // アクション
      toggleSidebar: () => set((state) => ({ sidebarVisible: !state.sidebarVisible })),

      setSidebarVisible: (visible: boolean) => set({ sidebarVisible: visible }),

      toggleDrawer: () => set((state) => ({ drawerOpen: !state.drawerOpen })),

      closeDrawer: () => set({ drawerOpen: false }),

      openDrawer: () => set({ drawerOpen: true }),

      setDistrictAccordionValue: (value: string[]) => set({ districtAccordionValue: value }),

      updateProducerAccordion: (districtKey: string, value: string[]) =>
        set((state) => ({
          producerAccordionValues: { ...state.producerAccordionValues, [districtKey]: value },
        })),
    }),
    {
      name: 'layout-storage',
      // sidebarVisible とアコーディオン状態を永続化（drawerOpenはセッション中のみ）
      partialize: (state) => ({
        sidebarVisible: state.sidebarVisible,
        districtAccordionValue: state.districtAccordionValue,
        producerAccordionValues: state.producerAccordionValues,
      }),
    }
  )
);
